import React, { useRef, useEffect, useState } from 'react';
import RetrievalPathGraph from './RetrievalPathGraph';
import '../styles/RetrievalPathPanel.css';

/**
 * Panel that slides in from the right to display retrieval path visualization
 */
function RetrievalPathPanel({ retrievalData, onClose, theme }) {
  const panelRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  if (!retrievalData) {
    return (
      <div className="retrieval-path-panel-overlay">
        <div className="retrieval-path-panel" ref={panelRef}>
          <div className="retrieval-path-panel-header">
            <h2>Retrieval Path Visualization</h2>
            <button className="panel-close-btn" onClick={onClose}>
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="retrieval-path-panel-content empty">
            <div className="empty-state">
              <span className="material-icons">account_tree</span>
              <p>No retrieval path data available</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="retrieval-path-panel-overlay">
      <div className="retrieval-path-panel" ref={panelRef}>
        <div className="retrieval-path-panel-header">
          <h2>Retrieval Path Visualization</h2>
          <button className="panel-close-btn" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>
        
        <div className="retrieval-path-panel-info">
          <div className="info-item">
            <span className="info-label">Session:</span>
            <span className="info-value">{retrievalData.sessionId}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Documents:</span>
            <span className="info-value">{retrievalData.documentCount}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Time:</span>
            <span className="info-value">{retrievalData.timestamp}</span>
          </div>
        </div>
        
        <div className="retrieval-path-panel-content">
          <RetrievalPathGraph 
            retrievalData={retrievalData} 
            theme={theme}
            onNodeSelect={setSelectedNode}
          />
          
          {selectedNode && (
            <div className="node-details">
              <div className="node-details-header">
                <h3>{selectedNode.type.charAt(0).toUpperCase() + selectedNode.type.slice(1)} Details</h3>
                <button className="close-details-btn" onClick={() => setSelectedNode(null)}>
                  <span className="material-icons">close</span>
                </button>
              </div>
              <div className="node-details-content">
                {selectedNode.type === 'document' && (
                  <>
                    <div className="details-item">
                      <span className="details-label">Source:</span>
                      <span className="details-value">{selectedNode.source || 'N/A'}</span>
                    </div>
                    <div className="details-item">
                      <span className="details-label">Filename:</span>
                      <span className="details-value">{selectedNode.filename || 'N/A'}</span>
                    </div>
                    {selectedNode.page && (
                      <div className="details-item">
                        <span className="details-label">Page:</span>
                        <span className="details-value">{selectedNode.page}</span>
                      </div>
                    )}
                    {selectedNode.relevanceScore !== undefined && (
                      <div className="details-item">
                        <span className="details-label">Relevance:</span>
                        <span className="details-value">{(selectedNode.relevanceScore * 100).toFixed(1)}%</span>
                      </div>
                    )}
                    <div className="content-preview-container">
                      <h4>Content:</h4>
                      <div className="content-preview">
                        {selectedNode.contentFull || selectedNode.content}
                      </div>
                    </div>
                  </>
                )}
                {(selectedNode.type === 'question' || selectedNode.type === 'answer') && (
                  <div className="content-preview-container">
                    <div className="content-preview">
                      {selectedNode.content}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RetrievalPathPanel;